// packages import
import { Box, Card, CardContent } from '@mui/material'
import { FC, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

// component import
import { ContentHead } from '../../../components/common/ContentHead'
import { EmptyData } from '../../../components/common/EmptyData'
import RecordPaymentFilters from '../../../components/billing/recordPayment/RecordPaymentFilters'
import { MatterBillsSection } from '../../../components/billing/recordPayment/MatterBillsSection'

// others
import { CardBox } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { useFetchDataForRecordPaymentsLazyQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'

export const RecordPayment: FC = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize state from query params
  const [matterId, setMatterId] = useState(() => searchParams.get('matterId') || '')
  const [billId, setBillId] = useState(() => searchParams.get('billId') || '')
  const [clientId, setClientId] = useState(() => searchParams.get('clientId') || '')
  const [hasSearched, setHasSearched] = useState(false)

  const [fetchDataForRecordPayments, { loading, data }] = useFetchDataForRecordPaymentsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      // Handle error silently or show notification
    }
  })

  // Auto-search on mount if query params exist
  useEffect(() => {
    const matterIdParam = searchParams.get('matterId')
    const billIdParam = searchParams.get('billId')
    const clientIdParam = searchParams.get('clientId')

    if (matterIdParam || billIdParam || clientIdParam) {
      setHasSearched(true)
      fetchDataForRecordPayments({
        variables: {
          fetchDataForRecordPaymentsInput: {
            matterId: matterIdParam || undefined,
            billId: billIdParam ? parseInt(billIdParam) : undefined,
            clientId: clientIdParam || undefined
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = () => {
    if (!matterId && !billId && !clientId) {
      return
    }

    // Update query params
    const newParams = new URLSearchParams()
    if (matterId) newParams.set('matterId', matterId)
    if (billId) newParams.set('billId', billId)
    if (clientId) newParams.set('clientId', clientId)
    setSearchParams(newParams, { replace: true })

    setHasSearched(true)
    fetchDataForRecordPayments({
      variables: {
        fetchDataForRecordPaymentsInput: {
          matterId: matterId || undefined,
          billId: billId ? parseInt(billId) : undefined,
          clientId: clientId || undefined
        }
      }
    })
  }

  const handleClear = () => {
    setMatterId('')
    setBillId('')
    setClientId('')
    setHasSearched(false)
    // Clear query params
    setSearchParams({}, { replace: true })
  }

  const cases = hasSearched ? data?.fetchDataForRecordPayments?.cases || [] : []
  const client = hasSearched ? data?.fetchDataForRecordPayments?.client : null
  const isEmpty = cases.length === 0 && !loading && hasSearched

  return (
    <CardBox marginTop={3}>
      <ContentHead title="Record Payment 2" />

      <Card sx={{ marginTop: 2, marginBottom: 2 }}>
        <CardContent>
          <RecordPaymentFilters
            matterId={matterId}
            billId={billId}
            clientId={clientId}
            onMatterIdChange={setMatterId}
            onBillIdChange={setBillId}
            onClientIdChange={setClientId}
            onSearch={handleSearch}
            onClear={handleClear}
            loading={loading}
          />
        </CardContent>
      </Card>

      {!hasSearched && (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title="Use the filter above to find matters, bills, or clients." />
        </Box>
      )}

      {isEmpty && (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title="No data found" />
        </Box>
      )}

      {cases.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          {cases
            .filter((caseItem): caseItem is NonNullable<typeof caseItem> => caseItem !== null && caseItem !== undefined)
            .map(caseItem => (
              <MatterBillsSection
                key={caseItem.id}
                caseItem={caseItem}
                client={client || undefined}
                loading={loading}
              />
            ))}
        </Box>
      )}
    </CardBox>
  )
}

export default RecordPayment
